import { DollarSign } from 'lucide-react'
import type { PaymentFunnelData } from '../types'

interface PaymentFunnelProps {
  data: PaymentFunnelData
}

function formatCurrency(val: number): string {
  return `$${val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

const BRACKET_COLORS = [
  'bg-gray-400',
  'bg-blue-400',
  'bg-indigo-500',
  'bg-purple-500',
  'bg-green-500',
]

export function PaymentFunnel({ data }: PaymentFunnelProps) {
  const maxBracket = Math.max(...data.by_revenue_bracket.map((b) => b.count), 1)

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">Payment Funnel</h3>
        <div className="flex items-center gap-1.5 bg-green-50 px-2.5 py-1 rounded-full">
          <DollarSign className="w-3.5 h-3.5 text-green-600" />
          <span className="text-xs font-semibold text-green-700">
            Avg ticket: {formatCurrency(data.avg_ticket)}
          </span>
        </div>
      </div>

      {/* Jobs → Payments funnel */}
      <div className="flex items-center gap-3 mb-4 text-center">
        <div className="flex-1 bg-blue-50 rounded-lg p-3">
          <p className="text-2xl font-bold text-blue-700">{data.matched_jobs}</p>
          <p className="text-xs text-blue-600">Matched Jobs</p>
        </div>
        <svg className="w-5 h-5 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <div className="flex-1 bg-green-50 rounded-lg p-3">
          <p className="text-2xl font-bold text-green-700">{data.jobs_with_payments}</p>
          <p className="text-xs text-green-600">With Payments</p>
        </div>
        <svg className="w-5 h-5 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <div className="flex-1 bg-emerald-50 rounded-lg p-3">
          <p className="text-2xl font-bold text-emerald-700">{formatCurrency(data.total_revenue)}</p>
          <p className="text-xs text-emerald-600">Revenue</p>
        </div>
      </div>

      {/* Revenue brackets */}
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Revenue per Job
      </h4>
      <div className="space-y-1.5 mb-4">
        {data.by_revenue_bracket.map((bracket, i) => {
          const widthPct = Math.max((bracket.count / maxBracket) * 100, 6)
          return (
            <div key={bracket.bracket} className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-16 text-right">{bracket.bracket}</span>
              <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                <div
                  className={`h-full ${BRACKET_COLORS[i] || BRACKET_COLORS[0]} rounded transition-all duration-500 flex items-center pl-2`}
                  style={{ width: `${widthPct}%` }}
                >
                  {widthPct > 15 && (
                    <span className="text-xs font-medium text-white">{bracket.count}</span>
                  )}
                </div>
              </div>
              {widthPct <= 15 && (
                <span className="text-xs text-gray-500">{bracket.count}</span>
              )}
            </div>
          )
        })}
      </div>

      {/* Payment types */}
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Payment Methods
      </h4>
      <div className="space-y-1.5">
        {data.by_payment_type.map((pt) => (
          <div key={pt.type} className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{pt.type}</span>
            <span className="font-medium text-gray-700">
              {formatCurrency(pt.amount)} <span className="text-xs text-gray-400">({pt.count})</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
